import { z } from 'zod'

export const WORK_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query values are shape-checked without taking ownership of domain validation order. */
export const workReadQuerySchema = z.object({
  orgId: optionalQueryString,
  user_id: optionalQueryString,
  project_id: optionalQueryString,
  assignee_user_id: optionalQueryString,
  team_id: optionalQueryString,
}).passthrough()

const readSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const submissionSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  period: z.enum(['daily', 'weekly']),
  submissionDate: z.string(),
  summary: z.string().nullable(),
  blockers: z.string().nullable(),
  nextSteps: z.string().nullable(),
  status: z.enum(['draft', 'submitted', 'approved', 'rejected']),
  approvedByUserId: z.string().nullable(),
  approvedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

const submissionReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('db1'),
  tables: z.tuple([z.literal('wvs_submissions')]),
  db1Read: z.literal(true),
  db1Written: z.literal(false),
  rawPayloadReturned: z.literal(false),
  aiParsed: z.literal(false),
  notificationSent: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const workSubmissionsListResponseSchema = z.object({
  contract_version: z.literal(WORK_READ_CONTRACT_VERSION),
  submissions: z.array(submissionSummarySchema),
  submissionEvidence: submissionReadEvidenceSchema,
  sideEffects: readSideEffectsSchema,
}).passthrough()

const taskSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  createdByUserId: z.string(),
  assigneeUserId: z.string().nullable(),
  projectId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: z.enum(['todo', 'in_progress', 'blocked', 'done', 'cancelled']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  sourceSubmissionId: z.string().nullable(),
  dueDate: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

const taskReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('db1'),
  tables: z.tuple([z.literal('wvs_tasks')]),
  db1Read: z.literal(true),
  db1Written: z.literal(false),
  rawPayloadReturned: z.literal(false),
  aiParsed: z.literal(false),
  notificationSent: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const workTasksListResponseSchema = z.object({
  contract_version: z.literal(WORK_READ_CONTRACT_VERSION),
  tasks: z.array(taskSummarySchema),
  taskEvidence: taskReadEvidenceSchema,
  sideEffects: readSideEffectsSchema,
}).passthrough()

const dashboardEvidenceSchema = z.object({
  sourceOfTruth: z.literal('db1'),
  tables: z.tuple([
    z.literal('wvs_submissions'),
    z.literal('wvs_tasks'),
    z.literal('tt_teams'),
    z.literal('tt_team_members'),
  ]),
  db1Read: z.literal(true),
  db1Written: z.literal(false),
  teamScoped: z.boolean(),
  aiParsed: z.literal(false),
  notificationSent: z.literal(false),
  deliveryRetryProcessed: z.literal(false),
  rawPayloadReturned: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const dashboardSchema = z.object({
  orgId: z.string(),
  submissions: z.object({
    total: z.number().int(),
    draft: z.number().int(),
    submitted: z.number().int(),
    approved: z.number().int(),
    rejected: z.number().int(),
    latestSubmissionDate: z.string().nullable(),
  }).passthrough(),
  tasks: z.object({
    total: z.number().int(),
    todo: z.number().int(),
    inProgress: z.number().int(),
    blocked: z.number().int(),
    done: z.number().int(),
    cancelled: z.number().int(),
    overdue: z.number().int(),
  }).passthrough(),
  readModel: z.object({
    sourceTables: z.tuple([
      z.literal('wvs_submissions'),
      z.literal('wvs_tasks'),
      z.literal('tt_teams'),
      z.literal('tt_team_members'),
    ]),
    teamScoped: z.boolean(),
    teamId: z.string().nullable(),
  }).passthrough(),
}).passthrough()

export const workDashboardResponseSchema = z.object({
  contract_version: z.literal(WORK_READ_CONTRACT_VERSION),
  dashboard: dashboardSchema,
  dashboardEvidence: dashboardEvidenceSchema,
  sideEffects: readSideEffectsSchema,
}).passthrough()

export type WorkReadQuery = z.infer<typeof workReadQuerySchema>
export type WorkSubmissionsListResponse = z.infer<typeof workSubmissionsListResponseSchema>
export type WorkTasksListResponse = z.infer<typeof workTasksListResponseSchema>
export type WorkDashboardResponse = z.infer<typeof workDashboardResponseSchema>
